import React, { createContext, useEffect, useReducer } from 'react';

// third-party
import { jwtDecode } from 'jwt-decode';

// reducer - state management
import { LOGIN, LOGOUT } from '@/contexts/auth-reducer/actions';
import authReducer from '@/contexts/auth-reducer/auth';

// project import
import Loader from '@/components/base-component/Loader';
import Axios from '@/utils/Axios';
import { KeyedObject } from '@/types/base/root';
import { AuthProps, JWTContextType } from '@/types/bs-types/auth';

// constant
const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

const verifyToken: (st: string) => boolean = (accessToken) => {
  if (!accessToken) {
    return false;
  }
  const decoded: KeyedObject = jwtDecode(accessToken);
  return decoded.exp > Date.now() / 1000;
};

const setSession = (accessToken?: string | null) => {
  if (accessToken) {
    localStorage.setItem('accessToken', accessToken);
    Axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    localStorage.removeItem('accessToken');
    delete Axios.defaults.headers.common.Authorization;
  }
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext<JWTContextType | null>(null);

export const JWTProvider = ({ children }: { children: React.ReactElement }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const init = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');

        if (accessToken && verifyToken(accessToken)) {
          setSession(accessToken);
          const response = await Axios.get('/auth/me');

          const { user } = response.data.payload;
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user
            }
          });
        } else {
          dispatch({
            type: LOGOUT
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: LOGOUT
        });
      }
    };

    init();
  }, []);

  const login = async (username: string, password: string) => {
    const response = await Axios.post(
      '/auth/sign-in',
      { username, password },
      {
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE,PATCH,OPTIONS',
          'Content-Type': 'application/json',
          'Access-Control-Allow-Headers': '*'
        }
      }
    );

    const {
      payload: { accessToken, user }
    } = response.data;

    setSession(accessToken);

    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user
      }
    });
  };

  const register = async (payload: {
    username: string;
    password: string;
    account: {
      prefix?: string;
      prefixCustom?: string;
      firstName?: string;
      lastName?: string;
      fullName?: string;
      position?: string;
      positionName?: string;
      avatar?: string;
      email: string;
      phoneNumber?: string;
      address?: string;
      userRole?: string;
      permission?: string;
      paymentType?: string;
      accountNumber?: string;
      accountDisplayName?: string;
      bookBank?: string;
      promptPayNumber?: string;
    };
  }) => {
    const response = await Axios.post('/auth/register', payload, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  };
  const logout = () => {
    setSession(null);
    dispatch({ type: LOGOUT });
  };

  const resetPassword = async (email: string) => {
    console.log('email - ', email);
  };

  const updateProfile = () => {};

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return <JWTContext.Provider value={{ ...state, login, logout, register, resetPassword, updateProfile }}>{children}</JWTContext.Provider>;
};

export default JWTContext;
